import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomerTagsOfTeam } from "@redotech/redo-merchant-app-common/client/customer-tags";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  CustomerTagOperation,
  ManageCustomerTagsStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { useEffect, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface CustomerTagActionNodeData {
  deleteElements: (id: string) => void;
  step: PartialWithRequiredType<ManageCustomerTagsStep>;
}

export function CustomerTagActionNode(data: {
  id: string;
  data: CustomerTagActionNodeData;
  selected?: boolean;
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const team = useRequiredContext(TeamContext);

  const { step } = data.data;

  const isAddOperation = step.operation === CustomerTagOperation.ADD;
  const tagIds = useMemo(() => step.tagIds || [], [step.tagIds]);

  const [reloadTrigger, setReloadTrigger] = useState(0);

  const customerTagsLoad = useLoad(
    async (signal) =>
      getCustomerTagsOfTeam(client, { teamId: team._id }, signal),
    [team._id, reloadTrigger],
  );

  const { tagNames, hasUnknownTags } = useMemo(() => {
    if (tagIds.length === 0) {
      return { tagNames: "No tags selected", hasUnknownTags: false };
    }

    const foundTags: string[] = [];
    let hasUnknown = false;

    for (const tagId of tagIds) {
      const foundTag = customerTagsLoad.value?.find((tag) => tag._id === tagId);
      if (foundTag) {
        foundTags.push(foundTag.name);
      } else {
        foundTags.push(tagId);
        hasUnknown = true;
      }
    }

    let displayText: string;
    if (foundTags.length === 1) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      displayText = foundTags[0];
    } else if (foundTags.length <= 3) {
      displayText = foundTags.join(", ");
    } else {
      displayText = `${foundTags.slice(0, 2).join(", ")} +${foundTags.length - 2} more`;
    }

    return { tagNames: displayText, hasUnknownTags: hasUnknown };
  }, [tagIds, customerTagsLoad.value]);

  // Only reload when we detect unknown tag IDs (likely newly created)
  useEffect(() => {
    if (hasUnknownTags && !customerTagsLoad.pending) {
      setReloadTrigger((prev) => prev + 1);
    }
  }, [hasUnknownTags, customerTagsLoad.pending]);

  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        icon={<RedoFeaturedIcon color="success" Icon={TagIcon} size="xs" />}
        onDelete={() => data.data.deleteElements(data.id)}
        selected={data.selected}
        step={data.data.step}
        title="Manage customer tags"
      >
        <Flex dir="column" gap="xs">
          <Text fontSize="xxs">
            {isAddOperation ? "Add" : "Remove"}{" "}
            {tagIds.length === 1 ? "Tag" : "Tags"}
          </Text>
          <Text className={NodeCSS.valueText}>{tagNames}</Text>
        </Flex>
      </BaseNode>
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
